import css from './LoginForm.module.css';
import React, { useContext, useEffect } from 'react';
import { UserContext, UserType } from '../../../context/userContext';
import TextInput from '../../Common/TextInput/TextInput';
import Validators from '../../../utility/validators';
import {
  LoginPageAction,
  FormField,
  FormStatus,
} from '../../../types/appTypes';
import { useForm } from '../../../hooks/useForm';
import DataForm from '../../Common/DataForm/DataForm';

type LoginFormProps = {
  onRegister: (action: LoginPageAction) => void;
  onResetPassword: (action: LoginPageAction) => void;
  onLoginSuccess: () => void;
};

type LoginFormFields = {
  email: FormField<string>;
  password: FormField<string>;
};

const LoginForm = ({
  onRegister,
  onResetPassword,
  onLoginSuccess,
}: LoginFormProps) => {
  const { login } = useContext(UserContext);
  const { fields, formState, setFields, setFormState } =
    useForm<LoginFormFields>({
      initialFields: {
        email: { value: '', valid: false },
        password: { value: '', valid: false },
      },
    });

  const formIsValid = fields.email.valid && fields.password.valid;

  useEffect(() => {
    if (formState.status === FormStatus.Success) {
      onLoginSuccess();
    }
  }, [formState, onLoginSuccess]);

  const formRequiredInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    const valid = Validators.required(value);
    setFields((pv) => {
      return { ...pv, [name]: { value: value, valid: valid } };
    });
  };

  const registerHandler = (event: React.MouseEvent) => {
    onRegister(LoginPageAction.Register);
  };

  const resetPasswordHandler = (event: React.MouseEvent) => {
    onResetPassword(LoginPageAction.ForgotPassword);
  };

  const handleADLogin = () => {
    login!({ type: UserType.AD });
  };

  const formSubmitHandler = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formIsValid) {
      return;
    }

    setFormState({ status: FormStatus.Submitting });

    try {
      const user = await login!({
        type: UserType.Mongo,
        email: fields.email.value.toLowerCase(),
        password: fields.password.value,
      });
      if (user) {
        setFormState({ status: FormStatus.Success });
      } else {
        setFormState({ status: FormStatus.Fail });
      }
    } catch (error) {
      setFormState({ status: FormStatus.Error });
    }
  };

  return (
    <DataForm
      title=''
      submitErrorMessage='Username or password is invalid'
      secondaryContainerClass={css.container}
      status={formState.status}
      onSubmit={formSubmitHandler}
    >
      <div className={css.logo}>
        <img src="/logo/Atlas-logo-black.png" alt="Atlas logo" />
      </div>
      <div className={css.formGroup}>
        <TextInput
          placeholder='Email'
          type='email'
          name='email'
          value={fields.email.value}
          valid={fields.email.valid}
          onChange={formRequiredInputChangeHandler}
        />
        <TextInput
          placeholder='Password'
          type='password'
          name='password'
          value={fields.password.value}
          valid={fields.password.valid}
          onChange={formRequiredInputChangeHandler}
        />
      </div>
      <div className={css.actions}>
        <button type='submit' disabled={!formIsValid}>
          Sign In
        </button>
        <button type='button' onClick={registerHandler}>
          Create Account
        </button>
        <button type='button' onClick={handleADLogin} className={css.ssoButton}>
          <div className={css.ssoButtonContent}>
            <img src="/logo/LibertyMark.svg" alt="Liberty Mark" className={css.ssoButtonIcon} width={60} height='auto' />
            <span className={css.ssoButtonText}>Liberty Login</span>
          </div>
        </button>
      </div>
      <div className={css.links}>
        <p onClick={resetPasswordHandler}>Forgot Password?</p>
      </div>
    </DataForm>
  );
};

export default LoginForm;
